/**
 * Form rules — expression evaluator for `set_value` effect (0.37.24).
 *
 * When an admin writes a set_value with a string that starts with '='
 * (Excel-style), the rest is parsed and evaluated as a simple
 * arithmetic expression against the current form values. Otherwise
 * the value is used verbatim (backward compat with plain string /
 * number / boolean set_values).
 *
 *   Examples (admin writes these in the rule editor):
 *     =field1 + field2
 *     =field1.value + field2.value        // `.value` suffix accepted
 *     =(price * quantity) - discount
 *     =subtotal * 0.2
 *
 * Grammar:
 *   expr   := term  (('+' | '-') term)*
 *   term   := factor (('*' | '/') factor)*
 *   factor := number
 *          |  string                       // "..." or '...'  (0.37.25)
 *          |  identifier                   // resolves via values map
 *          |  identifier '(' args ')'      // function call     (0.37.25)
 *          |  '(' expr ')'
 *          |  '-' factor                   // unary minus
 *   args   := (expr (',' expr)*)?
 *
 * Deliberately NO scripting-language features (no if/else, no
 * loops, no variables, no property access beyond the optional
 * `.value` suffix, no anything that requires touching the browser
 * runtime). This is a calculator + narrow function library, not a
 * scripting environment — admin-authored content never gets
 * `eval()` or `new Function()`.
 *
 * Semantics:
 *   - Field references resolve to `values[fieldName]`.
 *   - Empty / null / undefined fields resolve to 0 (like Excel).
 *   - Non-numeric field values are `parseFloat`'d; unparseable → 0.
 *   - Division by zero returns NaN (not Infinity — safer downstream).
 *   - Any parse or evaluation error returns undefined; caller
 *     (evaluate.js) then skips the set_value effect entirely so the
 *     field keeps whatever value it had.
 *   - Errors are logged via console.warn so admin can debug.
 */
import { readValue } from '../readValue';

/**
 * True if `v` is a string starting with '='. Used by evaluate.js to
 * decide whether a set_value should go through the expression path.
 */
export function isExpression(v) {
  return typeof v === 'string' && v.length > 0 && v.charAt(0) === '=';
}

/**
 * Strip the leading '=' and return the raw expression source. Caller
 * checks isExpression() first; this trusts that.
 */
export function extractExpression(v) {
  return v.slice(1);
}

/* --------------------------------------------------------------
 * Tokenizer.
 * -------------------------------------------------------------- */

function tokenize(input) {
  const tokens = [];
  let i = 0;
  while (i < input.length) {
    const c = input.charAt(i);

    // Whitespace — skip.
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++;
      continue;
    }

    // Single-char operators + parens + comma (comma is only
    // meaningful as a function-call argument separator).
    // 0.38.24 — `&` is Excel-style string concatenation. Lowest
    // precedence (below +/-), applied by parseConcat. Each side
    // goes through cascade formatting so `total & " AMD"` picks
    // up the platform number-format default without an explicit
    // format() wrapper.
    if (c === '(' || c === ')' || c === '+' || c === '-'
     || c === '*' || c === '/' || c === ',' || c === '&') {
      tokens.push({ type: c });
      i++;
      continue;
    }

    // 0.38.94 - Comparison operators: = == != <> < <= > >=.
    // Two-char forms checked before single-char. These sit at a
    // precedence BELOW +/- and & (see parseComparison), and yield a
    // boolean (1/0) used mainly by if(cond, a, b). `=` and `==` are
    // both equality here - the leading `=` that marks an expression
    // was already stripped by extractExpression, so a bare `=` inside
    // the source is unambiguous as equality. `<>` and `!=` are both
    // "not equal" (Excel + C styles).
    if (c === '<' || c === '>' || c === '=' || c === '!') {
      const next = input.charAt(i + 1);
      let op = c;
      if ((c === '<' && (next === '=' || next === '>'))
       || (c === '>' && next === '=')
       || (c === '=' && next === '=')
       || (c === '!' && next === '=')) {
        op = c + next;
        i += 2;
      } else {
        // Bare '!' alone is not a valid token here.
        if (c === '!') throw new Error("Unexpected '!' (did you mean '!='?)");
        i += 1;
      }
      tokens.push({ type: 'cmp', op });
      continue;
    }

    // String literal — double or single quoted. NO escape sequences
    // (kept intentionally boring — admin can't type "\n" and get a
    // newline; if a library name contains a quote of one kind, use
    // the other kind to wrap it). Unterminated string is a parse
    // error.
    if (c === '"' || c === "'") {
      const quote = c;
      i++;
      let start = i;
      while (i < input.length && input.charAt(i) !== quote) i++;
      if (i >= input.length) {
        throw new Error(`Unterminated string starting at position ${start - 1}`);
      }
      tokens.push({ type: 'str', value: input.slice(start, i) });
      i++; // consume the closing quote
      continue;
    }

    // Number literal. Accepts "1", "1.5", ".5". Does NOT accept
    // scientific notation ("1e3") — kept intentionally boring;
    // admin-facing calculator doesn't need it.
    if ((c >= '0' && c <= '9') || (c === '.' && isDigit(input.charAt(i + 1)))) {
      let start = i;
      while (i < input.length && isDigit(input.charAt(i))) i++;
      if (input.charAt(i) === '.') {
        i++;
        while (i < input.length && isDigit(input.charAt(i))) i++;
      }
      const num = parseFloat(input.slice(start, i));
      if (isNaN(num)) throw new Error(`Bad number literal near position ${start}`);
      tokens.push({ type: 'num', value: num });
      continue;
    }

    // Identifier. Starts with letter or underscore; continues with
    // letters, digits, underscores, or dots (dots let admin write
    // `field.value` — the parser strips a trailing `.value` for
    // convenience so bare `field` and `field.value` mean the same
    // thing).
    if (isIdentStart(c)) {
      let start = i;
      i++;
      while (i < input.length && isIdentPart(input.charAt(i))) i++;
      let id = input.slice(start, i);
      if (id.endsWith('.value')) id = id.slice(0, -6);
      if (id === '') throw new Error(`Empty identifier near position ${start}`);
      tokens.push({ type: 'id', value: id });
      continue;
    }

    throw new Error(`Unexpected character '${c}' at position ${i}`);
  }
  return tokens;
}

function isDigit(c) { return c >= '0' && c <= '9'; }
function isIdentStart(c) {
  return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
}
function isIdentPart(c) {
  return isIdentStart(c) || isDigit(c) || c === '.';
}

/* --------------------------------------------------------------
 * Recursive-descent parser + evaluator (fused — we evaluate as we
 * parse since there's no need to produce an AST for anything else).
 * -------------------------------------------------------------- */

// 0.37.40 — Functions that operate on a field's NAME (to look up
// the element's options list, structure metadata, etc.) rather
// than its resolved value. The parser treats their FIRST argument
// specially: a bare identifier passes through as its raw name
// string instead of being resolved to the field's current value.
// String literals still work the same way (labels_of("field") ==
// labels_of(field)). Add a function name here if it needs the
// same treatment.
// 0.40.01 - options_count joins labels_of here so both accept a bare
// field name, e.g. options_count(nice_class), not just the quoted
// form. Without this the parser would resolve the identifier to the
// field's VALUE before the function saw it, and the function needs
// the NAME to look the element up in the structure.
const NAME_PASSTHROUGH_FUNCTIONS = new Set(['labels_of', 'options_count']);

function parse(tokens, values, functions) {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = (type) => {
    const t = tokens[pos];
    if (!t || t.type !== type) {
      throw new Error(`Expected '${type}', got '${t ? t.type : 'end of input'}'`);
    }
    pos++;
    return t;
  };

  // 0.37.31 — Coerce a value to a number for arithmetic operators.
  // Was: identifiers eagerly parseFloat'd inside parseFactor. That
  // broke bare field references used as STRING arguments to
  // functions like lib() — a dropdown holding "renewal_each" got
  // resolved to 0 before it reached lib()'s keyValue arg, so no
  // library row matched. Now parseFactor returns raw values
  // (string / number / boolean / etc.) and each arithmetic
  // operator coerces just before combining.
  //   - undefined / null / "" → 0 (empty ≡ zero, matches Excel)
  //   - numbers pass through
  //   - booleans → 1 / 0
  //   - strings run through parseFloat, NaN → 0
  //   - anything else → 0
  const toNum = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    }
    return 0;
  };

  const parseExpr = () => {
    let left = parseTerm();
    while (true) {
      const t = peek();
      if (!t || (t.type !== '+' && t.type !== '-')) break;
      pos++;
      const right = parseTerm();
      // 0.38.04 — Propagate undefined through arithmetic. If
      // either operand is undefined (typically a lib() call
      // whose row hasn't finished fetching), the whole
      // expression is undefined — resolveAdminText renders it
      // as empty string instead of "0". Once the fetch
      // resolves, real values flow and arithmetic runs
      // normally. Field values that are truly empty ("") still
      // toNum to 0 via the operator's coercion below.
      if (left === undefined || right === undefined) {
        left = undefined;
        continue;
      }
      // 0.37.31 — Coerce both sides at the operator so string field
      // values (like a dropdown code) don't silently JS-concat when
      // arithmetic is intended. Excel-style: `=` always means
      // arithmetic, never string concat.
      left = t.type === '+' ? toNum(left) + toNum(right) : toNum(left) - toNum(right);
    }
    return left;
  };

  const parseTerm = () => {
    let left = parseFactor();
    while (true) {
      const t = peek();
      if (!t || (t.type !== '*' && t.type !== '/')) break;
      pos++;
      const right = parseFactor();
      // 0.38.04 — Propagate undefined (see parseExpr).
      if (left === undefined || right === undefined) {
        left = undefined;
        continue;
      }
      // 0.37.31 — Coerce at the operator (same reasoning as
      // parseExpr's + / -).
      const l = toNum(left);
      const r = toNum(right);
      if (t.type === '*') {
        left = l * r;
      } else {
        // Division by zero: return NaN rather than Infinity. Downstream
        // set_value will still fire (NaN is a value); consumer field
        // will show NaN which admin can spot and fix.
        left = r === 0 ? NaN : l / r;
      }
    }
    return left;
  };

  // Comma-separated argument list up to the closing paren. Called
  // after we've consumed the '(' of a function call. Returns the
  // evaluated array of arg values.
  //
  // 0.37.40 — `firstArgIsName` flag: when true, the FIRST argument
  // is treated specially. If it's a bare identifier NOT followed
  // by '(' (so not a nested function call), pass the identifier
  // NAME as a string rather than resolving it to its value.
  // If it's a string literal, use its value directly. Anything
  // else (nested call, number, parenthesised expression) parses
  // normally. Used for functions like labels_of() that operate
  // on the field's name to look up options — see
  // NAME_PASSTHROUGH_FUNCTIONS below.
  const parseArgs = (firstArgIsName = false) => {
    const args = [];
    if (peek() && peek().type === ')') return args;
    if (firstArgIsName) {
      const t = peek();
      const nextIsOpenParen = tokens[pos + 1] && tokens[pos + 1].type === '(';
      if (t && t.type === 'id' && !nextIsOpenParen) {
        pos++;
        args.push(t.value); // raw name string
      } else if (t && t.type === 'str') {
        pos++;
        args.push(t.value);
      } else {
        // 0.38.94 - parse at the comparison level so a function arg
        // can itself be a comparison or concat (e.g.
        // if(a > b, "x", "y")). parseComparisonRef is assigned after
        // parseComparison is defined (all these are closures over the
        // same pos), so it's available by call time.
        args.push(parseComparisonRef());
      }
    } else {
      args.push(parseComparisonRef());
    }
    while (peek() && peek().type === ',') {
      pos++;
      args.push(parseComparisonRef()); // subsequent args resolve at full precedence
    }
    return args;
  };

  // 0.38.94 - Forward reference so parseArgs / parseFactor (defined
  // above parseComparison) can call the lowest-precedence parser.
  // Assigned once parseComparison is defined below.
  let parseComparisonRef = () => parseExpr();

  const parseFactor = () => {
    const t = peek();
    if (!t) throw new Error('Unexpected end of expression');
    if (t.type === '(') {
      pos++;
      // 0.38.94 - Parenthesised groups parse at the full comparison
      // level, so ( a > b ) and ( x & y ) work inside arithmetic.
      const v = parseComparisonRef();
      consume(')');
      return v;
    }
    if (t.type === '-') {
      pos++;
      // 0.37.31 — Coerce; negation only makes sense on a number.
      return -toNum(parseFactor());
    }
    if (t.type === '+') {
      pos++;
      // Unary plus is a no-op on numbers but forces coercion —
      // kept for symmetry with unary minus.
      return toNum(parseFactor());
    }
    if (t.type === 'num') {
      pos++;
      return t.value;
    }
    if (t.type === 'str') {
      // 0.37.25 — String literals are their own value. They only
      // appear as function-call arguments in practice (there's
      // nothing arithmetic to do with them); passing one to an
      // arithmetic operator will coerce via + (JS concat) or
      // NaN-out on * / -. That's acceptable — admin shouldn't be
      // mixing strings into arithmetic without a reason.
      pos++;
      return t.value;
    }
    if (t.type === 'id') {
      pos++;
      // 0.37.25 — Identifier followed by '(' is a function call.
      // Otherwise it's a field reference (existing behavior).
      if (peek() && peek().type === '(') {
        // 0.37.40 — Some functions (e.g. labels_of) work on the
        // field's NAME, not its resolved value — they need to look
        // up the element's options list. Historically admin had to
        // quote the name (labels_of("multiselect_1")), which is
        // ugly. Now the parser recognises a small set of "name
        // passthrough" functions and, for their FIRST argument
        // ONLY, if it's a bare identifier NOT followed by '(' (so
        // not itself a nested function call), passes the raw
        // identifier NAME to the function as a string. String
        // literals continue to work — admin who writes
        // labels_of("multiselect_1") sees the same behavior.
        const fnName = t.value;
        const namePassthroughFirstArg = NAME_PASSTHROUGH_FUNCTIONS.has(fnName);
        pos++;
        const args = parseArgs(namePassthroughFirstArg);
        consume(')');
        const fn = functions ? functions[fnName] : undefined;
        if (typeof fn !== 'function') {
          throw new Error(`Unknown function '${fnName}'`);
        }
        const result = fn(...args);
        // 0.38.04 — Return undefined verbatim when a function
        // returns undefined (typically lib() returning while the
        // library row is still being fetched). Was: coerced to 0
        // so arithmetic wouldn't silently NaN. But that produced
        // a visible "0" in TEXT contexts (labels, hints) — admin's
        // "shows 0 for a while" report. The arithmetic paths now
        // detect undefined at the operator level (see parseExpr /
        // parseTerm below) and propagate it up rather than
        // coercing, so `=lib(...) * multiplier` returns undefined
        // while lib() is loading and resolveAdminText renders it
        // as empty string. Once the fetch resolves, the correct
        // value flows.
        return result;
      }
      // 0.37.31 — Return the RAW field value (string / number /
      // whatever). Was: parseFloat'd inline, which broke bare
      // field references passed as string args to functions
      // (e.g. `lib(..., dropdown_mark_type, ...)` where the
      // dropdown holds a string code — parseFloat turned it into
      // 0 before lib() ever saw it, so the library lookup never
      // matched). Coercion now happens at arithmetic operators
      // (see toNum above), so field values only get numberified
      // when arithmetic actually demands it.
      // 0.45.89 - and a dotted name reaches into an object value.
      const raw = values ? readValue(values, t.value) : undefined;
      /* 0.48.89 - A path the object does not have (an address without a
         building in the API answer) reads as empty text, so the rest of
         a line still shows. A name that is nothing at all stays unknown
         (still loading, or a typo). */
      if (raw === undefined && values && String(t.value).includes('.')) {
        const parts = String(t.value).split('.');
        for (let i = parts.length - 1; i >= 1; i -= 1) {
          const base = values[parts.slice(0, i).join('.')];
          if (base && typeof base === 'object') return '';
        }
      }
      return raw;
    }
    throw new Error(`Unexpected token '${t.type}'`);
  };

  // 0.38.24 — parseConcat: Excel-style `&` string concatenation.
  // Lowest precedence, wraps parseExpr. Each operand goes through
  // functions.concat (which itself runs the cascade on every
  // fragment), so `total & " AMD"` picks up the platform's number
  // format automatically. If functions.concat isn't available
  // (older callers that didn't wire it up), falls back to raw
  // String() concatenation — safe default, at worst you get
  // "120000 AMD" instead of "120,000 AMD".
  const parseConcat = () => {
    let left = parseExpr();
    while (true) {
      const t = peek();
      if (!t || t.type !== '&') break;
      pos++;
      const right = parseExpr();
      // Propagate undefined the same way arithmetic does — a lib()
      // still fetching should keep the whole `&` expression
      // undefined so resolveAdminText renders empty, not "undefined AMD".
      if (left === undefined || right === undefined) {
        left = undefined;
        continue;
      }
      if (functions && typeof functions.concat === 'function') {
        left = functions.concat(left, right);
      } else {
        left = (left == null ? '' : String(left)) + (right == null ? '' : String(right));
      }
    }
    return left;
  };

  // 0.38.94 - parseComparison: the LOWEST-precedence level, wrapping
  // parseConcat. A single comparison operator ( = == != <> < <= > >= )
  // between two concat-expressions yields 1 (true) or 0 (false).
  // Non-associative in practice (admin writes one comparison), but
  // implemented left-associative so `a < b < c` doesn't throw - it
  // evaluates `(a<b)` (0/1) then compares to c, which is rarely
  // intended but harmless.
  //
  // Comparison semantics:
  //   - If BOTH sides are numeric-looking, compare as numbers.
  //   - Otherwise compare as strings (so ="cash" = dropdown_x works).
  //   - undefined propagates (a lib() still loading keeps the whole
  //     comparison undefined, same as arithmetic).
  const _looksNumeric = (v) => {
    if (typeof v === 'number') return true;
    if (typeof v === 'string' && v.trim() !== '') {
      return !isNaN(parseFloat(v)) && isFinite(Number(v));
    }
    return false;
  };
  const compare = (op, l, r) => {
    const bothNum = _looksNumeric(l) && _looksNumeric(r);
    let a, b;
    if (bothNum) { a = Number(l); b = Number(r); }
    else {
      a = (l === undefined || l === null) ? '' : String(l);
      b = (r === undefined || r === null) ? '' : String(r);
    }
    switch (op) {
      case '=':
      case '==': return a === b ? 1 : 0;
      case '!=':
      case '<>': return a !== b ? 1 : 0;
      case '<':  return a < b ? 1 : 0;
      case '<=': return a <= b ? 1 : 0;
      case '>':  return a > b ? 1 : 0;
      case '>=': return a >= b ? 1 : 0;
      default: throw new Error(`Unknown comparison '${op}'`);
    }
  };
  const parseComparison = () => {
    let left = parseConcat();
    while (true) {
      const t = peek();
      if (!t || t.type !== 'cmp') break;
      pos++;
      const right = parseConcat();
      if (left === undefined || right === undefined) {
        left = undefined;
        continue;
      }
      left = compare(t.op, left, right);
    }
    return left;
  };

  parseComparisonRef = parseComparison;
  const result = parseComparison();
  if (pos < tokens.length) {
    throw new Error(`Unexpected trailing token '${tokens[pos].type}'`);
  }
  return result;
}

/**
 * Public entry point. Takes the raw expression source (without the
 * leading '='), the current values map, and an optional functions
 * registry (name → JS function). Returns whatever the expression
 * evaluated to, or undefined if the expression can't be parsed —
 * caller should skip the set_value effect in that case so the field
 * keeps its current value.
 *
 * Note: successful evaluation may still return undefined for pure
 * arithmetic (e.g. `parseFloat('foo')` → NaN). We only return the
 * outer undefined on PARSE failure.
 */
// 0.46.18 - A formula whose result is itself a formula (text starting
// with "=") is worked out too. That is what a library value holding a
// formula gives: `=lib("texts", "key", "mark_type", "value")` returns
// "=if(...)", which was shown as text. Two levels deep at most, so a
// value that names itself cannot loop.
const MAX_NESTED_FORMULAS = 2;

export function evaluateExpression(source, values, functions, _depth = 0) {
  try {
    const tokens = tokenize(source);
    if (tokens.length === 0) return undefined;
    const out = parse(tokens, values, functions);
    if (isExpression(out) && _depth < MAX_NESTED_FORMULAS) {
      return evaluateExpression(extractExpression(out), values, functions, _depth + 1);
    }
    return out;
  } catch (e) {
    // Log for admin debugging but don't break the form. The rule
    // evaluator sees `undefined` and skips the set_value effect.
    // eslint-disable-next-line no-console
    console.warn(`[rule expression] "${source}" —`, e.message);
    return undefined;
  }
}
